import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Bell, Eye, Home, Send, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router';
import apiClient from '../services/apiClient';
import {
  Button,
  Badge,
  ConfirmDialog,
  Input,
  Modal,
  Textarea,
  SelectField,
  PageHeader,
  Spinner,
} from '../components/ui';
import type { ConferenceEvent } from '@conference/shared';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { adminEventService } from '../services/adminEventService';

const notifSchema = z.object({
  event_id: z.string().min(1, 'Select an event'),
  title:    z.string().min(3, 'Title is required'),
  message:  z.string().min(5, 'Message is required'),
  type:     z.enum(['info', 'reminder', 'update', 'alert', 'social']),
  audience: z.enum(['all', 'attendee', 'speaker', 'vendor']),
  home_expires_at: z.string().refine(
    (value) => !value || !Number.isNaN(new Date(value).getTime()),
    'Choose a valid date and time',
  ),
});
type NotifForm = z.infer<typeof notifSchema>;

interface NotificationBroadcastSummary {
  id: string;
  event_id: string | null;
  title: string;
  message: string;
  type: string;
  created_at: string;
  home_expires_at: string | null;
  retracted_at: string | null;
  audience: 'all' | 'attendee' | 'speaker' | 'vendor' | null;
  recipient_count: number | null;
  legacy: boolean;
}

const audienceLabels: Record<Exclude<NotificationBroadcastSummary['audience'], null>, string> = {
  all: 'All attendees',
  attendee: 'Attendees',
  speaker: 'Speakers',
  vendor: 'Vendors',
};

function lifecycleStatus(notification: NotificationBroadcastSummary) {
  if (notification.legacy) return 'Legacy notification';
  if (notification.retracted_at) return 'Retracted';
  if (notification.recipient_count === 0) return 'No recipients';
  if (
    notification.home_expires_at
    && new Date(notification.home_expires_at).getTime() <= Date.now()
  ) return 'Notification Center only';
  return 'Active on Home';
}

function typeBadge(type: string) {
  const map: Record<string, 'info' | 'warning' | 'danger' | 'success' | 'purple' | 'default'> = {
    info:     'info',
    reminder: 'warning',
    update:   'purple',
    alert:    'danger',
    social:   'success',
  };
  return <Badge variant={map[type] ?? 'default'}>{type}</Badge>;
}

export default function NotificationsPage() {
  const { activeWorkspaceId } = useAdminAuth();
  const [searchParams] = useSearchParams();
  const requestedEventId = searchParams.get('event_id') ?? '';
  const [events, setEvents]       = useState<ConferenceEvent[]>([]);
  const [sent, setSent]           = useState<NotificationBroadcastSummary[]>([]);
  const [loading, setLoading]     = useState(true);
  const [sendLoading, setSending] = useState(false);
  const [selected, setSelected] = useState<NotificationBroadcastSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [retractConfirmOpen, setRetractConfirmOpen] = useState(false);
  const historyRequest = useRef(0);
  const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<NotifForm>({
    resolver: zodResolver(notifSchema),
    defaultValues: {
      event_id: requestedEventId,
      title: '',
      message: '',
      type: 'info',
      audience: 'all',
      home_expires_at: '',
    },
  });

  const selectedEventId = watch('event_id');

  async function loadEvents() {
    setLoading(true);
    try {
      const eventsData = activeWorkspaceId ? await adminEventService.list(activeWorkspaceId) : await apiClient.get<ConferenceEvent[]>('/events');
      const availableEvents = eventsData.filter((e) => ['published', 'live'].includes(e.status));
      setEvents(availableEvents);
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory(eventId: string) {
    const request = ++historyRequest.current;
    setSent([]);
    setSelected(null);
    if (!eventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await apiClient.get<NotificationBroadcastSummary[]>(`/admin/notifications?event_id=${eventId}`);
      if (request === historyRequest.current) setSent(rows);
    } catch (err) {
      if (request === historyRequest.current) {
        toast.error('Could not load notification history', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    } finally {
      if (request === historyRequest.current) setLoading(false);
    }
  }

  useEffect(() => { void loadEvents(); }, [activeWorkspaceId]);
  useEffect(() => {
    if (requestedEventId && events.some((availableEvent) => availableEvent.id === requestedEventId)) {
      setValue('event_id', requestedEventId);
    }
  }, [events, requestedEventId, setValue]);
  useEffect(() => { loadHistory(selectedEventId); }, [selectedEventId]);

  const onSubmit = async (data: NotifForm) => {
    setSending(true);
    try {
      const result = await apiClient.post<{ sent: number }>('/admin/send-notification', {
        event_id: data.event_id,
        title:    data.title,
        message:  data.message,
        type:     data.type,
        audience: data.audience,
        home_expires_at: data.home_expires_at
          ? new Date(data.home_expires_at).toISOString()
          : null,
      });
      toast.success(`Notification sent to ${result.sent} ${data.audience === 'all' ? 'attendees' : data.audience + 's'}`);
      reset({
        event_id: data.event_id,
        title: '',
        message: '',
        type: 'info',
        audience: 'all',
        home_expires_at: '',
      });
      loadHistory(data.event_id);
    } catch (err) {
      toast.error('Send failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setSending(false);
    }
  };

  const openDetails = async (notification: NotificationBroadcastSummary) => {
    if (notification.event_id !== selectedEventId) return;
    if (notification.legacy) {
      setSelected(notification);
      return;
    }
    setDetailLoading(notification.id);
    try {
      setSelected(await apiClient.get<NotificationBroadcastSummary>(
        `/admin/notifications/${notification.id}`,
      ));
    } catch (err) {
      toast.error('Could not load details', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setDetailLoading(null);
    }
  };

  const removeFromHome = async () => {
    if (!selected || selected.legacy || selected.event_id !== selectedEventId) return;
    setActionLoading(true);
    try {
      const updated = await apiClient.post<NotificationBroadcastSummary>(
        `/admin/notifications/${selected.id}/remove-from-home`,
        {},
      );
      setSelected(updated);
      await loadHistory(selectedEventId);
      toast.success('Removed from Home');
    } catch (err) {
      toast.error('Update failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const retractNotification = async () => {
    if (!selected || selected.legacy || selected.event_id !== selectedEventId) return;
    setActionLoading(true);
    try {
      const updated = await apiClient.post<NotificationBroadcastSummary>(
        `/admin/notifications/${selected.id}/retract`,
        {},
      );
      setSelected(updated);
      setRetractConfirmOpen(false);
      await loadHistory(selectedEventId);
      toast.success('Notification retracted');
    } catch (err) {
      toast.error('Update failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const selectedCanAppearOnHome = Boolean(
    selected
    && !selected.legacy
    && !selected.retracted_at
    && selected.recipient_count !== 0
    && (
      !selected.home_expires_at
      || new Date(selected.home_expires_at).getTime() > Date.now()
    ),
  );

  return (
    <div className="p-8">
      <PageHeader title="Notifications" description="Compose and send notifications to event attendees." />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Compose panel */}
        <div className="lg:col-span-2">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-white mb-4">Compose Notification</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <SelectField
                label="Event *"
                options={[
                  { value: '', label: '— Select event —' },
                  ...events.map((e) => ({ value: e.id, label: e.name })),
                ]}
                error={errors.event_id?.message}
                {...register('event_id')}
              />
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  label="Type"
                  options={['info','reminder','update','alert','social'].map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
                  {...register('type')}
                />
                <SelectField
                  label="Audience"
                  options={[
                    { value: 'all',       label: 'All attendees'  },
                    { value: 'attendee',  label: 'Attendees only' },
                    { value: 'speaker',   label: 'Speakers only'  },
                    { value: 'vendor',    label: 'Vendors only'   },
                  ]}
                  {...register('audience')}
                />
              </div>
              <Input label="Title *" placeholder="Session starting in 10 minutes" error={errors.title?.message} {...register('title')} />
              <Textarea label="Message *" placeholder="Don't miss the opening keynote…" rows={3} error={errors.message?.message} {...register('message')} />
              <Input
                type="datetime-local"
                label="Show on Home until"
                hint={`Uses your device time zone (${deviceTimeZone}). After this time it stays in Notifications but leaves Home.`}
                error={errors.home_expires_at?.message}
                {...register('home_expires_at')}
              />
              <Button type="submit" icon={<Send size={14} />} loading={sendLoading} className="w-full justify-center">
                Send Notification
              </Button>
            </form>
          </div>
        </div>

        {/* Sent history */}
        <div className="lg:col-span-3">
          <h2 className="text-sm font-semibold text-white mb-4">Sent Notifications</h2>
          {loading ? (
            <div className="flex justify-center py-12"><Spinner className="w-5 h-5 text-gray-500" /></div>
          ) : sent.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-center">
              <Bell size={24} className="text-gray-600 mb-2" />
              <p className="text-sm text-gray-400">No notifications sent yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sent.map((n) => (
                <div key={n.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {typeBadge(n.type)}
                        <p className="text-sm font-medium text-white truncate">{n.title}</p>
                      </div>
                      <p className="text-xs text-gray-400 line-clamp-2">{n.message}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
                        {n.audience && <span>{audienceLabels[n.audience]}</span>}
                        {n.recipient_count !== null && n.recipient_count > 0 && (
                          <span>
                            {`${n.recipient_count} ${n.recipient_count === 1 ? 'recipient' : 'recipients'}`}
                          </span>
                        )}
                        <span>{lifecycleStatus(n)}</span>
                      </div>
                      {n.home_expires_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(n.home_expires_at).getTime() <= Date.now() ? 'Home expired' : 'Home until'}{' '}
                          {format(new Date(n.home_expires_at), 'MMM d, yyyy, h:mm a')}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <p className="text-xs text-gray-500">
                        {format(new Date(n.created_at), 'MMM d, h:mm a')}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Eye size={12} />}
                        loading={detailLoading === n.id}
                        onClick={() => { void openDetails(n); }}
                      >
                        View details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={!!selected}
        onClose={() => {
          if (!actionLoading) {
            setSelected(null);
            setRetractConfirmOpen(false);
          }
        }}
        title="Notification details"
        description={selected?.title}
        size="lg"
      >
        {selected && (
          <div className="space-y-5">
            <p className="text-sm text-gray-200 whitespace-pre-wrap">{selected.message}</p>

            <dl className="grid grid-cols-2 gap-x-5 gap-y-4 rounded-xl border border-gray-800 bg-gray-950/40 p-4">
              <div>
                <dt className="text-xs text-gray-500">Status</dt>
                <dd className="text-sm text-gray-200 mt-1">{lifecycleStatus(selected)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Sent</dt>
                <dd className="text-sm text-gray-200 mt-1">
                  {format(new Date(selected.created_at), 'MMM d, yyyy, h:mm a')}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Audience</dt>
                <dd className="text-sm text-gray-200 mt-1">
                  {selected.audience ? audienceLabels[selected.audience] : 'Unknown (legacy)'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Delivery</dt>
                <dd className="text-sm text-gray-200 mt-1">
                  {selected.recipient_count === null
                    ? 'Unknown recipients'
                    : selected.recipient_count === 0
                      ? 'No recipients'
                      : `${selected.recipient_count} ${selected.recipient_count === 1 ? 'recipient' : 'recipients'}`}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-xs text-gray-500">Home visibility</dt>
                <dd className="text-sm text-gray-200 mt-1">
                  {selected.retracted_at
                    ? 'Retracted from attendee feeds'
                    : selected.home_expires_at
                      ? `${new Date(selected.home_expires_at).getTime() <= Date.now() ? 'Removed at' : 'Visible until'} ${format(new Date(selected.home_expires_at), 'MMM d, yyyy, h:mm a')}`
                      : 'No expiry'}
                </dd>
              </div>
            </dl>

            {selected.legacy && (
              <p className="text-xs text-amber-300 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                Legacy notification — reliable audience and broadcast metadata predates this feature, so lifecycle actions are unavailable.
              </p>
            )}

            {!selected.legacy && !selected.retracted_at && selected.recipient_count !== 0 && (
              <div className="border-t border-gray-800 pt-4 space-y-3">
                {selectedCanAppearOnHome && (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-xs text-gray-400">Keep it in Notification Center but remove it from Home.</p>
                    <Button
                      variant="secondary"
                      icon={<Home size={14} />}
                      loading={actionLoading}
                      onClick={() => { void removeFromHome(); }}
                    >
                      Remove from Home
                    </Button>
                  </div>
                )}
                <div className="flex items-center justify-between gap-4">
                  <p className="text-xs text-gray-400">Remove it from attendee feeds permanently.</p>
                  <Button
                    variant="danger"
                    icon={<Undo2 size={14} />}
                    disabled={actionLoading}
                    onClick={() => setRetractConfirmOpen(true)}
                  >
                    Retract notification
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={retractConfirmOpen}
        onClose={() => {
          if (!actionLoading) setRetractConfirmOpen(false);
        }}
        onConfirm={() => { void retractNotification(); }}
        loading={actionLoading}
        title="Retract notification?"
        description="This removes the notification from attendee Home and Notification Center. The admin audit record is preserved, but the action cannot be undone. Send a new correction notification if needed."
        confirmLabel="Retract notification"
      />
    </div>
  );
}
